import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BLOG_POSTS } from '@/mocks/blog';
import { BORGHI } from '@/mocks/borghi';
import SeoHead from '@/components/feature/SeoHead';
import ComprareCasaBody from './components/ComprareCasaBody';
import GenericArticleBody from './components/GenericArticleBody';
import BorghiPiuBelliBody from './components/BorghiPiuBelliBody';
import VivereMarcheBody from './components/VivereMarcheBody';
import InvestireImmobiliBody from './components/InvestireImmobiliBody';

const BlogDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();

  const lang = i18n.language as 'it' | 'en' | 'de';

  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.discoveringmarche.com';

  const getTitle = (p: typeof BLOG_POSTS[0]) => {
    if (lang === 'en') return p.titleEn;
    if (lang === 'de') return p.titleDe;
    return p.title;
  };

  const getExcerpt = (p: typeof BLOG_POSTS[0]) => {
    if (lang === 'en') return p.excerptEn;
    if (lang === 'de') return p.excerptDe;
    return p.excerpt;
  };

  const getCategoria = (p: typeof BLOG_POSTS[0]) => {
    if (lang === 'en') return p.categoriaEn;
    if (lang === 'de') return p.categoriaDe;
    return p.categoria;
  };

  const getData = (p: typeof BLOG_POSTS[0]) => {
    if (lang === 'en') return p.dataEn;
    if (lang === 'de') return p.dataDe;
    return p.data;
  };

  const getUpdatedAt = (p: typeof BLOG_POSTS[0]) => {
    if (lang === 'en') return p.updatedAtEn || p.updatedAt;
    if (lang === 'de') return p.updatedAtDe || p.updatedAt;
    return p.updatedAt;
  };

  const getReadingTime = (p: typeof BLOG_POSTS[0]) => {
    if (lang === 'en') return p.readingTimeEn || p.readingTime;
    if (lang === 'de') return p.readingTimeDe || p.readingTime;
    return p.readingTime;
  };

  const relatedPosts = BLOG_POSTS.filter((p) => p.slug !== slug && p.categoria === post.categoria).slice(0, 3);
  const relatedBorghiData = post.relatedBorghi.map((s) => BORGHI.find((b) => b.slug === s)).filter(Boolean) as typeof BORGHI;

  // Article FAQ for this specific article
  const articleFaqs = slug === 'comprare-casa-nelle-marche-guida-completa'
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: lang === 'en' ? 'How much does a house cost in Marche?' : lang === 'de' ? 'Wie viel kostet ein Haus in den Marken?' : 'Quanto costa una casa nelle Marche?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: lang === 'en'
                ? 'In April 2026 the average price is around €1,619/m², with variations between provinces: Ascoli Piceno around €1,829/m², Pesaro-Urbino €1,798/m², Ancona €1,582/m², Macerata €1,398/m² and Fermo €1,292/m².'
                : lang === 'de'
                ? 'Im April 2026 liegt der Durchschnittspreis bei etwa 1.619 Euro/m², mit Variationen zwischen den Provinzen: Ascoli Piceno etwa 1.829 Euro/m², Pesaro-Urbino 1.798 Euro/m², Ancona 1.582 Euro/m², Macerata 1.398 Euro/m² und Fermo 1.292 Euro/m².'
                : 'Ad aprile 2026 il prezzo medio è di circa 1.619 euro/m², con variazioni tra province: Ascoli Piceno circa 1.829 euro/m², Pesaro-Urbino 1.798 euro/m², Ancona 1.582 euro/m², Macerata 1.398 euro/m² e Fermo 1.292 euro/m².',
            },
          },
          {
            '@type': 'Question',
            name: lang === 'en' ? 'What taxes apply when buying a first home?' : lang === 'de' ? 'Welche Steuern gelten beim Kauf einer Erstwohnung?' : "Quali imposte si pagano per l'acquisto prima casa?",
            acceptedAnswer: {
              '@type': 'Answer',
              text: lang === 'en'
                ? 'When buying from a private individual: 2% registration tax on cadastral value (min. €1,000), plus €50 fixed mortgage tax and €50 fixed cadastral tax. With the price-value system, taxes can be significantly reduced.'
                : lang === 'de'
                ? 'Beim Kauf von einer Privatperson: 2% Registrierungssteuer auf den Katasterwert (Min. 1.000 Euro), zuzüglich 50 Euro fester Hypothekensteuer und 50 Euro fester Katastersteuer. Mit dem Preis-Wert-System können die Steuern deutlich gesenkt werden.'
                : "Acquistando da privato: imposta di registro 2% sul valore catastale (min. 1.000 euro), più 50 euro fissi di imposta ipotecaria e 50 euro fissi di imposta catastale. Con il prezzo-valore, le imposte possono essere ridotte in modo significativo.",
            },
          },
          {
            '@type': 'Question',
            name: lang === 'en' ? 'What is the mortgage interest rate in 2026?' : lang === 'de' ? 'Wie hoch ist der Hypothekenzins im Jahr 2026?' : "Qual è il tasso di interesse dei mutui nel 2026?",
            acceptedAnswer: {
              '@type': 'Answer',
              text: lang === 'en'
                ? 'As of April 2026, the ECB deposit rate is 2.00%. The average cost of new mortgages is around 3.4%. The best fixed-rate offers can be found around 3%-3.5%.'
                : lang === 'de'
                ? 'Stand April 2026 liegt der EZB-Einlagezins bei 2,00%. Die durchschnittlichen Kosten neuer Hypotheken liegen bei etwa 3,4%. Die besten Festzinsangebote finden sich bei etwa 3%-3,5%.'
                : 'Ad aprile 2026 il tasso sui depositi BCE è al 2,00%. Il costo medio dei nuovi mutui è di circa 3,4%. Le migliori offerte a tasso fisso si trovano intorno al 3%-3,5%.',
            },
          },
        ],
      }
    : slug === 'borghi-piu-belli-marche-2026'
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: lang === 'en'
              ? 'Which borgo won the Borgo dei Borghi 2026 title?'
              : lang === 'de'
              ? 'Welches Dorf hat den Titel Borgo dei Borghi 2026 gewonnen?'
              : 'Quale borgo ha vinto il titolo di Borgo dei Borghi 2026?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: lang === 'en'
                ? 'Cingoli, in the province of Macerata, won the Borgo dei Borghi 2026 title during the Rai programme Il Kilimangiaro in spring 2026. Known as the "Balcony of the Marche", it offers extraordinary panoramic views and a well-preserved medieval historic centre.'
                : lang === 'de'
                ? 'Cingoli in der Provinz Macerata hat den Titel Borgo dei Borghi 2026 während der Rai-Sendung Il Kilimangiaro im Frühjahr 2026 gewonnen. Bekannt als der "Balkon der Marken", bietet es außergewöhnliche Panoramablicke und ein gut erhaltenes mittelalterliches historisches Zentrum.'
                : 'Cingoli, in provincia di Macerata, ha vinto il titolo di Borgo dei Borghi 2026 durante il programma Rai Il Kilimangiaro nella primavera del 2026. Conosciuto come il "Balcone delle Marche", offre viste panoramiche straordinarie e un centro storico medievale ben conservato.',
            },
          },
          {
            '@type': 'Question',
            name: lang === 'en'
              ? 'How many borghi are certified in the Marche region?'
              : lang === 'de'
              ? 'Wie viele Dörfer sind in der Region Marken zertifiziert?'
              : "Quanti borghi sono certificati nella regione Marche?",
            acceptedAnswer: {
              '@type': 'Answer',
              text: lang === 'en'
                ? 'The Marche region has 32 localities certified by the association "I Borghi più belli d\'Italia", making it one of the Italian regions with the highest density of certified historic borghi. Additionally, several borghi hold the Bandiere Arancioni certification from the Touring Club Italiano.'
                : lang === 'de'
                ? 'Die Region Marken hat 32 von der Vereinigung "I Borghi più belli d\'Italia" zertifizierte Ortschaften und zählt damit zu den italienischen Regionen mit der höchsten Dichte zertifizierter historischer Dörfer. Darüber hinaus verfügen mehrere Dörfer über die Bandiere Arancioni-Zertifizierung des Touring Club Italiano.'
                : "La regione Marche conta 32 località certificate dall'associazione \"I Borghi più belli d'Italia\", risultando una delle regioni italiane con la maggiore densità di borghi storici certificati. Inoltre, diversi borghi possiedono anche la certificazione Bandiere Arancioni del Touring Club Italiano.",
            },
          },
          {
            '@type': 'Question',
            name: lang === 'en'
              ? 'What makes the Marche borghi unique compared to other Italian regions?'
              : lang === 'de'
              ? 'Was macht die Dörfer der Marken im Vergleich zu anderen italienischen Regionen einzigartig?'
              : "Cosa rende unici i borghi delle Marche rispetto ad altre regioni italiane?",
            acceptedAnswer: {
              '@type': 'Answer',
              text: lang === 'en'
                ? 'The Marche borghi are recognisable for their fortified medieval layout with still visible walls, the dominant use of red brick, belvedere squares overlooking the hills, and a rich system of noble palaces, churches, historic theatres and museums. They are not simple villages but compact little worlds, each with its own distinct identity, squeezed between the Adriatic and the Apennines.'
                : lang === 'de'
                ? 'Die Dörfer der Marken sind erkennbar an ihrer befestigten mittelalterlichen Anlage mit noch sichtbaren Mauern, der dominierenden Verwendung von rotem Backstein, Belvedere-Plätzen mit Blick auf die Hügel und einem reichen System von Adelspalästen, Kirchen, historischen Theatern und Museen. Sie sind keine einfachen Dörfer, sondern kompakte kleine Welten, jede mit eigener Identität, eingezwängt zwischen der Adria und den Apenninen.'
                : 'I borghi marchigiani si riconoscono per l\'impianto fortificato medievale con mura ancora visibili, l\'uso dominante del laterizio rosso, le piazze-belvedere affacciate sulle colline e il ricco sistema di palazzi nobiliari, chiese, teatri storici e musei. Non sono semplici paesini ma piccoli mondi compatti, ognuno con una propria identità distinta, stretti tra l\'Adriatico e gli Appennini.',
            },
          },
        ],
      }
    : slug === 'vivere-nelle-marche-qualita-vita'
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: lang === 'en'
              ? 'How much does a home cost in Marche compared to the national average?'
              : lang === 'de'
              ? 'Wie viel kostet ein Haus in den Marken im Vergleich zum nationalen Durchschnitt?'
              : 'Quanto costa una casa nelle Marche rispetto alla media nazionale?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: lang === 'en'
                ? 'In April 2026 the average residential property price in Marche is around €1,619 per square metre, against an Italian average of around €2,188 per square metre. This means the Marche market remains on average more accessible than the national figure.'
                : lang === 'de'
                ? 'Im April 2026 liegt der durchschnittliche Preis für Wohnimmobilien in den Marken bei etwa 1.619 Euro pro Quadratmeter, gegenüber einem italienischen Durchschnitt von etwa 2.188 Euro pro Quadratmeter. Das bedeutet, dass der Markt der Marken im Durchschnitt erschwinglicher bleibt als der nationale Wert.'
                : 'Ad aprile 2026 il prezzo medio degli immobili residenziali nelle Marche è di circa 1.619 euro al metro quadro, contro una media italiana di circa 2.188 euro al metro quadro. Il mercato marchigiano resta mediamente più accessibile rispetto al dato nazionale.',
            },
          },
          {
            '@type': 'Question',
            name: lang === 'en'
              ? 'Which Marche province has the best quality of life?'
              : lang === 'de'
              ? 'Welche Provinz der Marken hat die beste Lebensqualität?'
              : 'Quale provincia delle Marche ha la migliore qualità della vita?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: lang === 'en'
                ? 'According to the Sole 24 Ore Quality of Life Ranking 2025, Ascoli Piceno is the best Marche province in 22nd place out of 107. Macerata follows at 26th, Ancona at 30th, Pesaro-Urbino at 31st and Fermo at 47th. All five provinces place in the upper half of the national ranking.'
                : lang === 'de'
                ? 'Laut dem Sole 24 Ore Qualität-des-Lebens-Ranking 2025 ist Ascoli Piceno die beste Provinz der Marken auf dem 22. Platz von 107. Macerata folgt auf dem 26., Ancona auf dem 30., Pesaro-Urbino auf dem 31. und Fermo auf dem 47. Platz. Alle fünf Provinzen befinden sich in der oberen Hälfte der nationalen Rangliste.'
                : 'Secondo la classifica del Sole 24 Ore Qualità della Vita 2025, Ascoli Piceno è la migliore provincia marchigiana, alla 22ª posizione su 107. Macerata segue al 26°, Ancona al 30°, Pesaro-Urbino al 31° e Fermo al 47°. Tutte e cinque le province si collocano nella metà alta della graduatoria nazionale.',
            },
          },
          {
            '@type': 'Question',
            name: lang === 'en'
              ? 'How much are average rents in Marche?'
              : lang === 'de'
              ? 'Wie hoch sind die durchschnittlichen Mieten in den Marken?'
              : "Qual è l'affitto medio nelle Marche?",
            acceptedAnswer: {
              '@type': 'Answer',
              text: lang === 'en'
                ? 'Average regional rents are around €9.18 per month per square metre, against the Italian average of €14.45. The difference is significant, especially compared with large cities such as Milan or Rome.'
                : lang === 'de'
                ? 'Die durchschnittlichen regionalen Mieten liegen bei etwa 9,18 Euro pro Monat pro Quadratmeter, gegenüber dem italienischen Durchschnitt von 14,45 Euro. Der Unterschied ist signifikant, besonders im Vergleich zu großen Städten wie Mailand oder Rom.'
                : 'I canoni medi regionali si attestano intorno a 9,18 euro al mese al metro quadro, contro i 14,45 euro della media italiana. La differenza è significativa, soprattutto se confrontata con i valori delle grandi città.',
            },
          },
          {
            '@type': 'Question',
            name: lang === 'en'
              ? 'What is the healthcare system like in the Marche?'
              : lang === 'de'
              ? 'Wie ist das Gesundheitssystem in den Marken?'
              : 'Come è la sanità nelle Marche?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: lang === 'en'
                ? 'The Marche region has important healthcare reference facilities, particularly in Ancona and the main provincial capitals. The quality and speed of access can vary significantly between coast, city and inland areas. Waiting times for some specialist services and the lesser capillarity in smaller municipalities are the main challenges, as in many Italian regions.'
                : lang === 'de'
                ? 'Die Region Marken verfügt über wichtige Gesundheitseinrichtungen, insbesondere in Ancona und den Hauptstädten der Provinzen. Qualität und Schnelligkeit des Zugangs können zwischen Küste, Stadt und Hinterland erheblich variieren. Wartezeiten für einige Fachdienstleistungen und die geringere Dichte in kleineren Gemeinden sind die Hauptprobleme, wie in vielen italienischen Regionen.'
                : 'La regione Marche dispone di strutture sanitarie di riferimento importanti, in particolare ad Ancona e nei principali capoluoghi di provincia. La qualità e la rapidità di accesso possono variare sensibilmente tra costa, città e aree interne. I tempi di attesa per alcune prestazioni specialistiche e la minore capillarità nei comuni più piccoli sono le principali criticità, come in molte regioni italiane.',
            },
          },
          {
            '@type': 'Question',
            name: lang === 'en'
              ? 'Is a car necessary to live in the Marche?'
              : lang === 'de'
              ? 'Ist ein Auto notwendig, um in den Marken zu leben?'
              : "Serve l'auto per vivere nelle Marche?",
            acceptedAnswer: {
              '@type': 'Answer',
              text: lang === 'en'
                ? 'The coastal strip is well served by the A14 motorway and the Adriatic railway line, so a car is less essential in coastal cities. The hinterland, however, remains more dependent on private cars, especially for those who choose to live in a hilltop borgo or a country house. Ancona-Falconara Airport offers national and international connections, but less served than major hubs.'
                : lang === 'de'
                ? 'Die Küstenregion ist gut durch die Autobahn A14 und die Adriatische Eisenbahnlinie erschlossen, sodass ein Auto in Küstenstädten weniger unbedingt nötig ist. Das Hinterland bleibt jedoch stärker auf das private Auto angewiesen, besonders für diejenigen, die in einem Hügeldorf oder einem Landhaus leben möchten. Der Flughafen Ancona-Falconara bietet nationale und internationale Verbindungen, ist aber weniger gut angebunden als große Drehkreuze.'
                : 'La fascia costiera è ben servita dall\'autostrada A14 e dalla linea ferroviaria adriatica, quindi l\'auto è meno essenziale nelle città di mare. L\'entroterra, invece, resta più dipendente dall\'auto privata, soprattutto per chi sceglie di vivere in un borgo collinare o in una casa di campagna. L\'aeroporto di Ancona-Falconara offre collegamenti nazionali e internazionali, ma è meno servito rispetto agli scali maggiori.',
            },
          },
          {
            '@type': 'Question',
            name: lang === 'en'
              ? 'Which nationalities are buying property in the Marche?'
              : lang === 'de'
              ? 'Welche Nationalitäten kaufen Immobilien in den Marken?'
              : 'Quali nazionalità acquistano immobili nelle Marche?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: lang === 'en'
                ? 'According to 2025 data, requests from abroad grew by +3.32% compared to 2024. Americans lead the rankings, followed by Germans, Dutch, British and other European buyers. The most requested provinces are Macerata, Fermo and Ancona. The most sought-after municipalities include Petritoli, Ripatransone, Serra San Quirico, Amandola and Arcevia.'
                : lang === 'de'
                ? 'Laut Daten aus dem Jahr 2025 sind die Anfragen aus dem Ausland um +3,32% gegenüber 2024 gestiegen. Die Amerikaner führen die Rangliste an, gefolgt von Deutschen, Niederländern, Briten und anderen europäischen Käufern. Die meistgesuchten Provinzen sind Macerata, Fermo und Ancona. Die begehrtesten Gemeinden sind Petritoli, Ripatransone, Serra San Quirico, Amandola und Arcevia.'
                : 'Secondo i dati del 2025, le richieste dall\'estero sono cresciute del +3,32% rispetto al 2024. Gli americani guidano la classifica, seguiti da tedeschi, olandesi, britannici e altri acquirenti europei. Le province più richieste sono Macerata, Fermo e Ancona. I comuni più ricercati includono Petritoli, Ripatransone, Serra San Quirico, Amandola e Arcevia.',
            },
          },
          {
            '@type': 'Question',
            name: lang === 'en'
              ? 'What opportunities exist for entrepreneurs in the Marche?'
              : lang === 'de'
              ? 'Welche Chancen gibt es für Unternehmer in den Marken?'
              : 'Quali opportunità esistono per imprenditori nelle Marche?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: lang === 'en'
                ? 'Key opportunities include experiential and slow tourism (walking trails, cycle tourism, farm stays, wine tours), building renovation of historic properties, coworking and coliving for digital nomads, and territory enhancement through tourism communication, short-term rental management and cultural experiences.'
                : lang === 'de'
                ? 'Wichtige Chancen umfassen Erlebnis- und Langsamtourismus (Wanderwege, Radtourismus, Bauernhofaufenthalte, Weintouren), Gebäudesanierung historischer Immobilien, Coworking und Coliving für digitale Nomaden sowie territoriale Wertschöpfung durch Tourismuskommunikation, Kurzzeitmietmanagement und Kulturerlebnisse.'
                : 'Le opportunità principali includono il turismo esperienziale e lento (cammini, cicloturismo, agriturismo, tour del vino), il recupero edilizio di immobili storici, il coworking e coliving per nomadi digitali, e la valorizzazione del territorio attraverso comunicazione turistica, gestione di affitti brevi ed esperienze culturali.',
            },
          },
          {
            '@type': 'Question',
            name: lang === 'en'
              ? 'What are the main risks to consider before moving to the Marche?'
              : lang === 'de'
              ? 'Was sind die Hauptrisiken, die vor dem Umzug in die Marken zu bedenken sind?'
              : 'Quali sono i principali rischi da considerare prima di trasferirsi nelle Marche?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: lang === 'en'
                ? 'The main risks are seismic risk in inland Apennine areas (especially after the 2016-2017 sequence), depopulation of hill and mountain borghi with reduced services, slower east-west connections, and a job market concentrated in specific sectors (mechanics, manufacturing, tourism) with fewer opportunities in technology and finance.'
                : lang === 'de'
                ? 'Die Hauptrisiken sind das Erdbebenrisiko im apenninischen Hinterland (besonders nach der Sequenz 2016-2017), die Entvölkerung von Hügel- und Bergdörfern mit reduzierten Dienstleistungen, langsamere Ost-West-Verbindungen und ein auf bestimmte Sektoren konzentrierter Arbeitsmarkt (Mechanik, Fertigung, Tourismus) mit weniger Chancen in Technologie und Finanzen.'
                : 'I principali rischi sono il rischio sismico nelle aree interne appenniniche (soprattutto dopo la sequenza 2016-2017), lo spopolamento dei borghi collinari e montani con riduzione dei servizi, i collegamenti est-ovest più lenti, e un mercato del lavoro concentrato in settori specifici (meccanica, manifattura, turismo) con meno opportunità in tecnologia e finanza.',
            },
          },
          {
            '@type': 'Question',
            name: lang === 'en'
              ? 'Is the Marche a good choice for remote workers?'
              : lang === 'de'
              ? 'Sind die Marken eine gute Wahl für Fernarbeiter?'
              : 'Le Marche sono una buona scelta per chi lavora da remoto?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: lang === 'en'
                ? 'Yes, remote working is the condition that makes moving to the Marche possible for many professionals. The region offers a more balanced life, direct relationship with the landscape, sustainable housing costs and a less frenetic daily dimension. However, internet coverage must be checked case by case, especially in more isolated inland areas.'
                : lang === 'de'
                ? 'Ja, die Fernarbeit ist die Bedingung, die den Umzug in die Marken für viele Fachleute möglich macht. Die Region bietet ein ausgewogeneres Leben, eine direkte Beziehung zur Landschaft, tragbare Wohnkosten und eine weniger hektische Alltagsdimension. Die Internetabdeckung muss jedoch im Einzelfall überprüft werden, besonders in abgelegeneren Hinterlandgebieten.'
                : 'Sì, il lavoro da remoto è la condizione che rende possibile il trasferimento nelle Marche per molti professionisti. La regione offre una vita più equilibrata, un rapporto diretto con il paesaggio, costi abitativi sostenibili e una dimensione quotidiana meno frenetica. La copertura internet, tuttavia, va verificata caso per caso, soprattutto nelle aree interne più isolate.',
            },
          },
        ],
      }
    : slug === 'investire-immobili-marche-2026'
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: lang === 'en'
              ? 'How much does a property cost on average in the Marche region?'
              : lang === 'de'
              ? 'Wie viel kostet eine Immobilie im Durchschnitt in der Region Marken?'
              : 'Quanto costa in media un immobile nella regione Marche?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: lang === 'en'
                ? 'As of April 2026, the average asking price for homes in Marche is around €1,619 per square metre, with Fermo being the most affordable province and Ascoli Piceno and Pesaro-Urbino recording higher values. Average rents stand at about €9.18 per square metre per month.'
                : lang === 'de'
                ? 'Stand April 2026 liegt der durchschnittliche geforderte Preis für Wohnungen in den Marken bei etwa 1.619 € pro Quadratmeter, wobei Fermo die erschwinglichste Provinz ist und Ascoli Piceno sowie Pesaro-Urbino höhere Werte verzeichnen. Die durchschnittlichen Mieten liegen bei etwa 9,18 € pro Quadratmeter pro Monat.'
                : 'Ad aprile 2026 il prezzo medio richiesto per le abitazioni nelle Marche è di circa 1.619 € al metro quadro, con Fermo tra le province più accessibili e Ascoli Piceno e Pesaro-Urbino che registrano valori più alti. Gli affitti medi si attestano a circa 9,18 € al metro quadro al mese.',
            },
          },
          {
            '@type': 'Question',
            name: lang === 'en'
              ? 'Why are foreign buyers increasingly interested in Marche real estate?'
              : lang === 'de'
              ? 'Warum sind ausländische Käufer zunehmend an Immobilien in den Marken interessiert?'
              : 'Perché gli acquirenti stranieri sono sempre più interessati agli immobili nelle Marche?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: lang === 'en'
                ? 'In 2025, foreign real estate enquiries in Marche grew by +3.32% while the national trend fell by -5.89%. Americans lead the rankings, followed by Germans, Dutch and British buyers. Popular borghi include Petritoli, Ripatransone, Serra San Quirico, Amandola and Arcevia, which offer sustainable prices, landscape, authenticity and strong territorial identity.'
                : lang === 'de'
                ? 'Im Jahr 2025 sind die ausländischen Immobilienanfragen in den Marken um +3,32% gestiegen, während der nationale Trend um -5,89% gefallen ist. Amerikaner führen die Rangliste an, gefolgt von deutschen, niederländischen und britischen Käufern. Beliebte Dörfer sind Petritoli, Ripatransone, Serra San Quirico, Amandola und Arcevia, die erschwingliche Preise, Landschaft, Authentizität und starke territoriale Identität bieten.'
                : 'Nel 2025 le richieste immobiliari dall\'estero nelle Marche sono cresciute del +3,32% mentre il trend nazionale registrava un calo del -5,89%. Gli americani guidano la classifica, seguiti da tedeschi, olandesi e britannici. I borghi più ricercati includono Petritoli, Ripatransone, Serra San Quirico, Amandola e Arcevia, che offrono prezzi sostenibili, paesaggio, autenticità e forte identità territoriale.',
            },
          },
          {
            '@type': 'Question',
            name: lang === 'en'
              ? 'Which areas of the Marche are best for real estate investment?'
              : lang === 'de'
              ? 'Welche Gebiete der Marken eignen sich am besten für Immobilieninvestitionen?'
              : 'Quali zone delle Marche sono migliori per investire in immobili?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: lang === 'en'
                ? 'The coast (Senigallia, Numana, Sirolo, San Benedetto, Fano, Pesaro) is best for short-term rentals and second homes but has higher prices. Medium-sized cities and well-connected centres (Ancona, Jesi, Civitanova, Macerata, Ascoli Piceno) suit traditional long-term rentals. The hinterland and borghi offer the most fascinating opportunities with lower entry prices and strong narrative potential for tourism projects, though connections and renovation costs must be carefully evaluated.'
                : lang === 'de'
                ? 'Die Küste (Senigallia, Numana, Sirolo, San Benedetto, Fano, Pesaro) ist am besten für Kurzzeitmieten und Zweitwohnungen geeignet, hat aber höhere Preise. Mittelgroße Städte und gut angebundene Zentren (Ancona, Jesi, Civitanova, Macerata, Ascoli Piceno) eignen sich für traditionelle Langzeitmieten. Das Hinterland und die Dörfer bieten die faszinierendsten Chancen mit niedrigeren Einstiegspreisen und starkem Potenzial für Tourismusprojekte, obwohl Verbindungen und Renovierungskosten sorgfältig geprüft werden müssen.'
                : 'La costa (Senigallia, Numana, Sirolo, San Benedetto, Fano, Pesaro) è ideale per affitti brevi e seconde case ma ha prezzi più alti. Le città medie e i centri ben collegati (Ancona, Jesi, Civitanova, Macerata, Ascoli Piceno) si adattano alla locazione tradizionale. L\'entroterra e i borghi offrono le opportunità più affascinanti con prezzi di ingresso più bassi e forte potenziale narrativo per progetti turistici, sebbene i collegamenti e i costi di ristrutturazione vadano valutati con attenzione.',
            },
          },
          {
            '@type': 'Question',
            name: lang === 'en'
              ? 'Is rental income the only return on real estate investment in Marche?'
              : lang === 'de'
              ? 'Ist Mieteinnahme die einzige Rendite bei Immobilieninvestitionen in den Marken?'
              : 'Il rendimento da locazione è l\'unico guadagno da un investimento immobiliare nelle Marche?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: lang === 'en'
                ? 'No. In Marche, value can also come from appreciation over time, renovation of historic properties, tourist appeal, foreign demand and the possibility of building a lifestyle project around the property. A renovated farmhouse or a house in a panoramic borgo is not just square metres — it becomes a real estate product with a precise identity, especially for foreign buyers seeking a lifestyle rather than just a house.'
                : lang === 'de'
                ? 'Nein. In den Marken kann der Wert auch aus Wertsteigerung im Laufe der Zeit, Renovierung historischer Immobilien, touristischer Attraktivität, ausländischer Nachfrage und der Möglichkeit kommen, ein Lifestyle-Projekt um die Immobilie herum aufzubauen. Ein renoviertes Bauernhaus oder ein Haus in einem panoramischen Dorf sind nicht nur Quadratmeter — es wird ein Immobilienprodukt mit präziser Identität, besonders für ausländische Käufer, die einen Lebensstil und nicht nur ein Haus suchen.'
                : 'No. Nelle Marche il valore può arrivare anche da rivalutazione nel tempo, recupero di immobili storici, attrattività turistica, domanda estera e possibilità di costruire un progetto di vita attorno alla casa. Un casolare ristrutturato o una casa in un borgo panoramico non sono solo metri quadri — diventano un prodotto immobiliare con identità precisa, soprattutto per gli acquirenti stranieri che cercano uno stile di vita e non solo una casa.',
            },
          },
        ],
      }
    : undefined;

  const articleJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: getTitle(post),
      description: getExcerpt(post),
      image: post.image,
      datePublished: post.data,
      dateModified: post.updatedAt || post.data,
      author: {
        '@type': 'Person',
        name: 'Ines Felicity Di Nallo',
        jobTitle: lang === 'en' ? 'Real Estate Agent' : lang === 'de' ? 'Immobilienmaklerin' : 'Agente Immobiliare',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Marche Casa',
        logo: 'https://public.readdy.ai/ai/img_res/49b48721-69a4-4c7d-8e06-4224313d172b.png',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${siteUrl}/blog` },
        { '@type': 'ListItem', position: 3, name: getTitle(post), item: `${siteUrl}/blog/${post.slug}` },
      ],
    },
  ];

  if (articleFaqs) {
    articleJsonLd.push(articleFaqs as any);
  }

  const ArticleBody = slug === 'comprare-casa-nelle-marche-guida-completa'
    ? ComprareCasaBody
    : slug === 'borghi-piu-belli-marche-2026'
    ? BorghiPiuBelliBody
    : slug === 'vivere-nelle-marche-qualita-vita'
    ? VivereMarcheBody
    : slug === 'investire-immobili-marche-2026'
    ? InvestireImmobiliBody
    : GenericArticleBody;

  return (
    <main className="min-h-screen bg-white">
      <SeoHead
        title={`${getTitle(post)} | Blog Marche Casa`}
        description={getExcerpt(post)}
        keywords={`${post.categoria}, borghi Marche, immobili Marche, ${post.slug.replace(/-/g, ' ')}`}
        canonical={`/blog/${post.slug}`}
        ogImage={post.image}
        jsonLd={articleJsonLd}
      />
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={post.image}
            alt={getTitle(post)}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10"></div>
        </div>
        <div className="relative z-10 w-full max-w-4xl mx-auto px-6 pb-16">
          <nav className="flex items-center gap-2 mb-6" aria-label="breadcrumb">
            <Link to="/blog" className="font-sans text-xs text-white/60 hover:text-white transition-colors whitespace-nowrap">Blog</Link>
            <i className="ri-arrow-right-s-line text-white/40 text-xs"></i>
            <span className="font-sans text-xs text-white/80 whitespace-nowrap">{getCategoria(post)}</span>
          </nav>
          <span className="inline-block font-sans text-xs tracking-widest uppercase text-terracotta-200 bg-terracotta-300/30 border border-terracotta-300/40 px-3 py-1.5 rounded-full mb-5">
            {getCategoria(post)}
          </span>
          <h1 className="font-serif text-3xl md:text-5xl text-white font-light leading-tight mb-4">
            {getTitle(post)}
          </h1>
          <div className="flex flex-wrap items-center gap-4">
            <p className="font-sans text-sm text-white/60">{getData(post)}</p>
            {getReadingTime(post) && (
              <span className="font-sans text-xs text-white/50 flex items-center gap-1.5">
                <i className="ri-time-line text-white/40"></i>
                {getReadingTime(post)}
              </span>
            )}
            {getUpdatedAt(post) && (
              <span className="font-sans text-xs text-white/50 flex items-center gap-1.5">
                <i className="ri-refresh-line text-white/40"></i>
                {lang === 'en' ? 'Updated' : lang === 'de' ? 'Aktualisiert' : 'Aggiornato'}: {getUpdatedAt(post)}
              </span>
            )}
          </div>
          {/* Author */}
          <div className="mt-5 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-terracotta-300/40 border border-terracotta-200/40 flex items-center justify-center">
              <i className="ri-user-line text-white/70 text-sm"></i>
            </div>
            <div>
              <p className="font-sans text-xs text-white/80">Ines Felicity Di Nallo</p>
              <p className="font-sans text-[10px] text-white/40 uppercase tracking-wider">
                {lang === 'en' ? 'Real Estate Agent' : lang === 'de' ? 'Immobilienmaklerin' : 'Agente Immobiliare'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section
        className="max-w-7xl mx-auto px-6 py-16"
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Article */}
          <article className="lg:col-span-2">
            <ArticleBody post={post} lang={lang} relatedBorghiData={relatedBorghiData} />
          </article>

          {/* Sidebar */}
          <aside className="lg:col-span-1 space-y-8">
            {/* Author card */}
            <div className="bg-ivory-50 border border-ivory-200 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-terracotta-100 flex items-center justify-center">
                  <i className="ri-user-line text-terracotta-300 text-lg"></i>
                </div>
                <div>
                  <p className="font-serif text-base text-anthracite-700">Ines Felicity Di Nallo</p>
                  <p className="font-sans text-xs text-anthracite-400">
                    {lang === 'en' ? 'Real Estate Agent CCIAA Ancona' : lang === 'de' ? 'Immobilienmaklerin CCIAA Ancona' : 'Agente Immobiliare CCIAA Ancona'}
                  </p>
                </div>
              </div>
              <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                {lang === 'en'
                  ? 'Expert in the Marche real estate market, specialised in historic borghi and international buyers.'
                  : lang === 'de'
                  ? 'Expertin für den Immobilienmarkt der Marken, spezialisiert auf historische Dörfer und internationale Käufer.'
                  : 'Esperta del mercato immobiliare marchigiano, specializzata in borghi storici e acquirenti internazionali.'
                }
              </p>
            </div>

            {/* CTA */}
            <div className="bg-terracotta-50 border border-terracotta-200 rounded-2xl p-6">
              <h3 className="font-serif text-xl text-anthracite-800 mb-3">{t('blog.sidebar.cta.title')}</h3>
              <p className="font-sans text-sm text-anthracite-500 mb-5 leading-relaxed">{t('blog.sidebar.cta.text')}</p>
              <Link
                to="/contatti"
                className="block text-center font-sans text-sm tracking-widest uppercase px-6 py-3 bg-terracotta-300 text-white hover:bg-terracotta-400 transition-colors rounded-full cursor-pointer whitespace-nowrap"
              >
                {t('blog.sidebar.cta.btn')}
              </Link>
            </div>

            {/* Related posts */}
            {relatedPosts.length > 0 && (
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-5">{t('blog.correlati')}</p>
                <div className="space-y-5">
                  {relatedPosts.map((rp) => (
                    <Link key={rp.slug} to={`/blog/${rp.slug}`} className="group flex gap-4 cursor-pointer">
                      <div className="w-20 h-16 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={rp.image} alt={getTitle(rp)} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                      </div>
                      <div>
                        <p className="font-sans text-xs text-anthracite-400 mb-1">{getData(rp)}</p>
                        <p className="font-serif text-sm text-anthracite-700 leading-snug group-hover:text-terracotta-300 transition-colors line-clamp-2">
                          {getTitle(rp)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>
    </main>
  );
};

export default BlogDetailPage;